// src/api.js
// Use relative URL - proxied through React dev server (port 2000 -> 3001)
import { getAuthToken } from './auth/AuthContext';

const API_BASE_URL = "/api";

/**
 * Get default headers including auth token
 */
const getHeaders = (extra = {}) => {
  const headers = { ...extra };
  const token = getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

const handleResponse = async (response) => {
  // If unauthorized, redirect to login
  if (response.status === 401) {
    localStorage.removeItem('dmp_token');
    window.location.reload();
    throw new Error('Session expired');
  }
  if (!response.ok) {
    const errorBody = await response.text();
    console.error(`API Error (${response.status}):`, errorBody);
    throw new Error(`HTTP error! status: ${response.status}, body: ${errorBody}`);
  }
  return response.json();
};

const fetchWithRetry = async (url, options = {}, retries = 3, delay = 1000) => {
  // Inject auth headers
  options.headers = getHeaders(options.headers || {});
  try {
    const response = await fetch(url, options);
    return handleResponse(response);
  } catch (error) {
    if (retries > 0 && !error.message.includes('Session expired')) {
      await new Promise(resolve => setTimeout(resolve, delay));
      return fetchWithRetry(url, options, retries - 1, delay * 2);
    }
    throw error;
  }
};

const fetchScenariosWithDetails = async () => {
  const url = `${API_BASE_URL}/scenarios_with_details`;
  try {
    const data = await fetchWithRetry(url);
    console.log('Raw data from API:', data);

    const transformedData = data.map(scenario => {
      // Build unified items[] array - single source of truth
      const items = [
        ...scenario.individuals.map(individual => ({
          // Type identifier
          type: 'individual',
          // Standard ID format
          id: individual.individual_id,
          uniqueBoxId: `individual-${individual.individual_id}`,
          // Rendering fields
          content: 'Individu',
          headerColor: '#FF6F61',
          laneId: individual.lane_position,
          boxPosition: individual.box_position,
          // ALL database fields
          ...individual
        })),
        ...scenario.entities.map(entity => ({
          // Type identifier
          type: 'entity',
          // Standard ID format
          id: entity.entity_id,
          uniqueBoxId: `entity-${entity.entity_id}`,
          // Rendering fields
          content: 'Entity',
          headerColor: '#FFD700',
          laneId: entity.lane_position,
          boxPosition: entity.box_position,
          // ALL database fields
          ...entity
        }))
      ];

      // boxes is now an alias for items (backward compatibility)
      const boxes = items;

      const relations = scenario.shareholdings.map(shareholding => {
        // Determine owner type and create canonical uniqueBoxId
        const isIndividualOwner = shareholding.individual_owner_id != null;
        const ownerId = shareholding.individual_owner_id || shareholding.entity_owner_id;
        const ownerBoxId = isIndividualOwner ? `individual-${ownerId}` : `entity-${ownerId}`;
        const ownedBoxId = `entity-${shareholding.owned_id}`;

        return {
          id: `${ownerBoxId}-${ownedBoxId}`,
          dbId: shareholding.id, // Database ID for updates
          startBox: {
            laneId: boxes.find(b => b.uniqueBoxId === ownerBoxId)?.laneId,
            boxId: ownerBoxId
          },
          endBox: {
            laneId: boxes.find(b => b.uniqueBoxId === ownedBoxId)?.laneId,
            boxId: ownedBoxId
          },
          percentage: shareholding.percentage,
          role: shareholding.role || 'ASSOCIE',
          isManager: shareholding.is_manager || false,
          receiveDividends: shareholding.receive_dividends !== false,
          entryDate: shareholding.entry_date,
          exitDate: shareholding.exit_date,
          dividendTaxOption: shareholding.dividend_tax_option || 'PFU',
          acompteDispense: shareholding.acompte_dispense || false
        };
      });

      console.log('Transformed relations:', relations);

      const lanes = [...new Set(boxes.map(box => box.laneId))].map(laneId => ({
        id: laneId,
        level: laneId
      })).sort((a, b) => a.id - b.id);

      return {
        ...scenario,
        items,      // New unified array (single source of truth)
        boxes,      // Alias for items (backward compatibility)
        relations,
        lanes,
        selectedBoxes: []
      };
    });

    console.log('Final transformed data:', transformedData);
    return transformedData;
  } catch (error) {
    console.error("Error in fetchScenariosWithDetails:", error);
    throw error;
  }
};

const createScenario = async (data) => {
  const url = `${API_BASE_URL}/scenarios`;
  try {
    const response = await fetchWithRetry(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return response;
  } catch (error) {
    console.error("Error in createScenario:", error);
    throw error;
  }
};

const updateScenario = async (id, data) => {
  const url = `${API_BASE_URL}/scenarios/${id}`;
  try {
    const response = await fetchWithRetry(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return response;
  } catch (error) {
    console.error("Error in updateScenario:", error);
    throw error;
  }
};

const deleteScenario = async (id) => {
  const url = `${API_BASE_URL}/scenarios/${id}`;
  try {
    const response = await fetchWithRetry(url, {
      method: 'DELETE',
    });
    return response;
  } catch (error) {
    console.error("Error in deleteScenario:", error);
    throw error;
  }
};

const fetchFields = async (type) => {
  const url = `${API_BASE_URL}/${type}`;
  try {
    return await fetchWithRetry(url);
  } catch (error) {
    console.error("Error in fetchFields:", error);
    throw error;
  }
};

const getIndividual = async (id) => {
  const url = `${API_BASE_URL}/individus/${id}`;
  try {
    return await fetchWithRetry(url);
  } catch (error) {
    console.error("Error in getIndividual:", error);
    throw error;
  }
};

const getIndividualsByScenario = async (scenarioId) => {
  const url = `${API_BASE_URL}/scenarios/${scenarioId}/individuals`;
  try {
    return await fetchWithRetry(url);
  } catch (error) {
    console.error("Error in getIndividualsByScenario:", error);
    throw error;
  }
};

const createIndividual = async (data) => {
  const url = `${API_BASE_URL}/individus`;
  try {
    const response = await fetchWithRetry(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return response;
  } catch (error) {
    console.error("Error in createIndividual:", error);
    throw error;
  }
};

const updateIndividual = async (id, data) => {
  const url = `${API_BASE_URL}/individus/${id}`;
  try {
    const response = await fetchWithRetry(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return response;
  } catch (error) {
    console.error("Error in updateIndividual:", error);
    throw error;
  }
};

// Create individual from template (copies template data + incomes to scenario)
const createIndividualFromTemplate = async (data) => {
  const url = `${API_BASE_URL}/individus/from-template`;
  try {
    const response = await fetchWithRetry(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return response;
  } catch (error) {
    console.error("Error in createIndividualFromTemplate:", error);
    throw error;
  }
};

// Entity CRUD operations
const getEntity = async (id) => {
  const url = `${API_BASE_URL}/entities/${id}`;
  try {
    return await fetchWithRetry(url);
  } catch (error) {
    console.error("Error in getEntity:", error);
    throw error;
  }
};

const createEntity = async (data) => {
  const url = `${API_BASE_URL}/entities`;
  try {
    const response = await fetchWithRetry(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return response;
  } catch (error) {
    console.error("Error in createEntity:", error);
    throw error;
  }
};

const updateEntity = async (id, data) => {
  const url = `${API_BASE_URL}/entities/${id}`;
  try {
    const response = await fetchWithRetry(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return response;
  } catch (error) {
    console.error("Error in updateEntity:", error);
    throw error;
  }
};

const deleteIndividual = async (id) => {
  const url = `${API_BASE_URL}/individuals/${id}`;
  try {
    const response = await fetchWithRetry(url, {
      method: 'DELETE',
    });
    return response;
  } catch (error) {
    console.error("Error in deleteIndividual:", error);
    throw error;
  }
};

const deleteEntity = async (id) => {
  const url = `${API_BASE_URL}/entities/${id}`;
  try {
    const response = await fetchWithRetry(url, {
      method: 'DELETE',
    });
    return response;
  } catch (error) {
    console.error("Error in deleteEntity:", error);
    throw error;
  }
};

// Create entity from template (copies template data to scenario)
const createEntityFromTemplate = async (data) => {
  const url = `${API_BASE_URL}/entities/from-template`;
  try {
    const response = await fetchWithRetry(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return response;
  } catch (error) {
    console.error("Error in createEntityFromTemplate:", error);
    throw error;
  }
};

// ==================== INDIVIDUAL TEMPLATES (Bibliothèque) ====================

const fetchIndividualTemplates = async () => {
  const url = `${API_BASE_URL}/individual-templates`;
  try {
    return await fetchWithRetry(url);
  } catch (error) {
    console.error("Error in fetchIndividualTemplates:", error);
    throw error;
  }
};

const createIndividualTemplate = async (data) => {
  const url = `${API_BASE_URL}/individual-templates`;
  try {
    const response = await fetchWithRetry(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return response;
  } catch (error) {
    console.error("Error in createIndividualTemplate:", error);
    throw error;
  }
};

const deleteIndividualTemplate = async (id) => {
  const url = `${API_BASE_URL}/individual-templates/${id}`;
  try {
    const response = await fetchWithRetry(url, {
      method: 'DELETE',
    });
    return response;
  } catch (error) {
    console.error("Error in deleteIndividualTemplate:", error);
    throw error;
  }
};

const getIndividualTemplate = async (id) => {
  const url = `${API_BASE_URL}/individual-templates/${id}`;
  try {
    return await fetchWithRetry(url);
  } catch (error) {
    console.error("Error in getIndividualTemplate:", error);
    throw error;
  }
};

const updateIndividualTemplate = async (id, data) => {
  const url = `${API_BASE_URL}/individual-templates/${id}`;
  try {
    const response = await fetchWithRetry(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return response;
  } catch (error) {
    console.error("Error in updateIndividualTemplate:", error);
    throw error;
  }
};

// ==================== ENTITY TEMPLATES (Bibliothèque) ====================

const fetchEntityTemplates = async () => {
  const url = `${API_BASE_URL}/entity-templates`;
  try {
    return await fetchWithRetry(url);
  } catch (error) {
    console.error("Error in fetchEntityTemplates:", error);
    throw error;
  }
};

const createEntityTemplate = async (data) => {
  const url = `${API_BASE_URL}/entity-templates`;
  try {
    const response = await fetchWithRetry(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return response;
  } catch (error) {
    console.error("Error in createEntityTemplate:", error);
    throw error;
  }
};

const deleteEntityTemplate = async (id) => {
  const url = `${API_BASE_URL}/entity-templates/${id}`;
  try {
    const response = await fetchWithRetry(url, {
      method: 'DELETE',
    });
    return response;
  } catch (error) {
    console.error("Error in deleteEntityTemplate:", error);
    throw error;
  }
};

const getEntityTemplate = async (id) => {
  const url = `${API_BASE_URL}/entity-templates/${id}`;
  try {
    return await fetchWithRetry(url);
  } catch (error) {
    console.error("Error in getEntityTemplate:", error);
    throw error;
  }
};

const updateEntityTemplate = async (id, data) => {
  const url = `${API_BASE_URL}/entity-templates/${id}`;
  try {
    const response = await fetchWithRetry(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return response;
  } catch (error) {
    console.error("Error in updateEntityTemplate:", error);
    throw error;
  }
};

// ==================== INCOMES (Revenus) ====================

const fetchIncomes = async (individualId) => {
  const url = `${API_BASE_URL}/individuals/${individualId}/incomes`;
  try {
    return await fetchWithRetry(url);
  } catch (error) {
    console.error("Error in fetchIncomes:", error);
    throw error;
  }
};

const createIncome = async (individualId, data) => {
  const url = `${API_BASE_URL}/individuals/${individualId}/incomes`;
  try {
    const response = await fetchWithRetry(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return response;
  } catch (error) {
    console.error("Error in createIncome:", error);
    throw error;
  }
};

const updateIncome = async (incomeId, data) => {
  const url = `${API_BASE_URL}/incomes/${incomeId}`;
  try {
    const response = await fetchWithRetry(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return response;
  } catch (error) {
    console.error("Error in updateIncome:", error);
    throw error;
  }
};

const deleteIncome = async (incomeId) => {
  const url = `${API_BASE_URL}/incomes/${incomeId}`;
  try {
    const response = await fetchWithRetry(url, {
      method: 'DELETE',
    });
    return response;
  } catch (error) {
    console.error("Error in deleteIncome:", error);
    throw error;
  }
};

// ==================== ENTITY FINANCIALS (Données financières) ====================

const fetchEntityFinancials = async (entityId) => {
  const url = `${API_BASE_URL}/entities/${entityId}/financials`;
  try {
    return await fetchWithRetry(url);
  } catch (error) {
    console.error("Error in fetchEntityFinancials:", error);
    throw error;
  }
};

const createEntityFinancials = async (entityId, data) => {
  const url = `${API_BASE_URL}/entities/${entityId}/financials`;
  try {
    const response = await fetchWithRetry(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return response;
  } catch (error) {
    console.error("Error in createEntityFinancials:", error);
    throw error;
  }
};

const updateEntityFinancials = async (financialsId, data) => {
  const url = `${API_BASE_URL}/entity-financials/${financialsId}`;
  try {
    const response = await fetchWithRetry(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return response;
  } catch (error) {
    console.error("Error in updateEntityFinancials:", error);
    throw error;
  }
};

const deleteEntityFinancials = async (financialsId) => {
  const url = `${API_BASE_URL}/entity-financials/${financialsId}`;
  try {
    const response = await fetchWithRetry(url, {
      method: 'DELETE',
    });
    return response;
  } catch (error) {
    console.error("Error in deleteEntityFinancials:", error);
    throw error;
  }
};

// ==================== ENTITY SOURCES (Sources de CA) ====================

const fetchEntitySources = async (entityId) => {
  const url = `${API_BASE_URL}/entities/${entityId}/sources`;
  try {
    return await fetchWithRetry(url);
  } catch (error) {
    console.error("Error in fetchEntitySources:", error);
    throw error;
  }
};

const createEntitySource = async (entityId, data) => {
  const url = `${API_BASE_URL}/entities/${entityId}/sources`;
  try {
    const response = await fetchWithRetry(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return response;
  } catch (error) {
    console.error("Error in createEntitySource:", error);
    throw error;
  }
};

const updateEntitySource = async (sourceId, data) => {
  const url = `${API_BASE_URL}/entity-sources/${sourceId}`;
  try {
    const response = await fetchWithRetry(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return response;
  } catch (error) {
    console.error("Error in updateEntitySource:", error);
    throw error;
  }
};

const deleteEntitySource = async (sourceId) => {
  const url = `${API_BASE_URL}/entity-sources/${sourceId}`;
  try {
    const response = await fetchWithRetry(url, {
      method: 'DELETE',
    });
    return response;
  } catch (error) {
    console.error("Error in deleteEntitySource:", error);
    throw error;
  }
};

const upsertEntitySourceYearly = async (sourceId, data) => {
  const url = `${API_BASE_URL}/entity-sources/${sourceId}/yearly`;
  try {
    const response = await fetchWithRetry(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return response;
  } catch (error) {
    console.error("Error in upsertEntitySourceYearly:", error);
    throw error;
  }
};

// ==================== SHAREHOLDINGS (Relations) ====================

const createShareholding = async (data) => {
  const url = `${API_BASE_URL}/shareholdings`;
  try {
    const response = await fetchWithRetry(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return response;
  } catch (error) {
    console.error("Error in createShareholding:", error);
    throw error;
  }
};

const updateShareholding = async (id, data) => {
  const url = `${API_BASE_URL}/shareholdings/${id}`;
  try {
    const response = await fetchWithRetry(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return response;
  } catch (error) {
    console.error("Error in updateShareholding:", error);
    throw error;
  }
};

const deleteShareholding = async (id, force = false) => {
  const url = `${API_BASE_URL}/shareholdings/${id}${force ? '?force=true' : ''}`;
  try {
    const response = await fetchWithRetry(url, {
      method: 'DELETE',
    });
    return response;
  } catch (error) {
    console.error("Error in deleteShareholding:", error);
    throw error;
  }
};

const getShareholding = async (id) => {
  const url = `${API_BASE_URL}/shareholdings/${id}`;
  try {
    const response = await fetchWithRetry(url, {
      method: 'GET',
    });
    return response;
  } catch (error) {
    console.error("Error in getShareholding:", error);
    throw error;
  }
};

const apiService = {
  fetchScenariosWithDetails,
  createScenario,
  updateScenario,
  deleteScenario,
  fetchFields,
  getIndividual,
  getIndividualsByScenario,
  createIndividual,
  updateIndividual,
  createIndividualFromTemplate,
  getEntity,
  createEntity,
  updateEntity,
  deleteIndividual,
  deleteEntity,
  createEntityFromTemplate,
  // Templates (Bibliothèque)
  fetchIndividualTemplates,
  createIndividualTemplate,
  deleteIndividualTemplate,
  getIndividualTemplate,
  updateIndividualTemplate,
  fetchEntityTemplates,
  createEntityTemplate,
  deleteEntityTemplate,
  getEntityTemplate,
  updateEntityTemplate,
  // Incomes (Revenus)
  fetchIncomes,
  createIncome,
  updateIncome,
  deleteIncome,
  // Entity Financials (Données financières)
  fetchEntityFinancials,
  createEntityFinancials,
  updateEntityFinancials,
  deleteEntityFinancials,
  // Entity Sources (Sources de CA)
  fetchEntitySources,
  createEntitySource,
  updateEntitySource,
  deleteEntitySource,
  upsertEntitySourceYearly,
  // Shareholdings (Relations)
  createShareholding,
  updateShareholding,
  deleteShareholding,
  getShareholding
};

export default apiService;